import z from 'zod/v4'

/** What the upload schemas check before the API validates the file itself. */
interface NamedFile {
  name: string
}

function isNamedFile(value: unknown): value is NamedFile {
  return (
    typeof value === 'object' && value !== null && 'name' in value && typeof value.name === 'string'
  )
}

export const customerImportFileSchema = z
  .custom<NamedFile>(isNamedFile, {
    message: 'Choose an .xlsx or .csv file.',
  })
  .refine((file) => /\.(xlsx|csv)$/i.test(file.name), {
    message: 'Choose an .xlsx or .csv file.',
  })

export const customerImportSourceSystemSchema = z.enum([
  'fsn',
  'the_floral_pos',
  'ftd',
  'floranext',
  'quickflora',
  'bloomnation',
  'generic_customer_csv',
])

export const customerImportRequestSchema = z.object({
  file: customerImportFileSchema,
  source_system: customerImportSourceSystemSchema,
})

export const customerImportAddressSchema = z
  .object({
    line1: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
    country: z.string(),
  })
  .nullable()

export const customerImportContactSchema = z.object({
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  is_primary: z.boolean(),
})

export const customerImportCustomerSchema = z.object({
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  is_business: z.boolean(),
  source_system: z.string().nullable(),
  source_key: z.string().nullable(),
  customer_since_on: z.string().nullable(),
  physical_address: customerImportAddressSchema,
  contacts: z.array(customerImportContactSchema),
})

export const customerImportRowSchema = z.object({
  row_number: z.number().int().positive(),
  status: z.enum(['ready', 'imported', 'skipped', 'invalid']),
  reason: z.string().nullable(),
  customer_id: z.number().int().positive().nullable(),
  customer: customerImportCustomerSchema,
})

export const customerImportSummarySchema = z.object({
  ready: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
})

export const customerImportResponseSchema = z.object({
  summary: customerImportSummarySchema,
  created_customer_ids: z.array(z.number().int().positive()),
  rows: z.array(customerImportRowSchema),
  rows_returned: z.number().int().nonnegative().optional(),
  rows_truncated: z.boolean().default(false),
})

export const customerImportAttemptProgressSchema = z.object({
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().int().min(0).max(100),
})

export const customerImportAttemptSchema = z.object({
  id: z.uuid(),
  status: z.enum(['queued', 'running', 'completed', 'failed']),
  stage: z.enum(['queued', 'importing_customers', 'completed', 'failed']),
  stage_label: z.string(),
  message: z.string().nullable(),
  source_system: customerImportSourceSystemSchema,
  source_label: z.string(),
  progress: customerImportAttemptProgressSchema,
  result: customerImportResponseSchema.nullable(),
  error: z
    .object({
      message: z.string(),
    })
    .nullable(),
  queued_at: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
})

export const customerImportActiveAttemptResponseSchema = z.object({
  attempt: customerImportAttemptSchema.nullable(),
})

export const productImportSourceSystemSchema = z.enum(['fsn'])
export const productImportScopeSchema = z.enum(['active_pages', 'all_active'])
export const productImportWebsiteNavigationModeSchema = z.enum(['auto', 'skip'])

export const productImportFileSchema = z
  .custom<NamedFile>(isNamedFile, {
    message: 'Choose an .xlsx or .csv file.',
  })
  .refine((file) => /\.(xlsx|csv)$/i.test(file.name), {
    message: 'Choose an .xlsx or .csv file.',
  })

export const productImportRequestSchema = z.object({
  file: productImportFileSchema,
  source_system: productImportSourceSystemSchema,
  import_scope: productImportScopeSchema,
  website_navigation: productImportWebsiteNavigationModeSchema,
})

/**
 * Money, a decimal string (`ProductImportService` sends prices through `Money::wire()`). Import
 * results stored before that hold JSON numbers, which read as the same decimal string.
 */
const productImportPriceSchema = z.union([
  z.string().regex(/^-?\d+\.\d{2}$/),
  z.number().transform((price) => price.toFixed(2)),
])

export const productImportVariantSchema = z.object({
  name: z.string(),
  price: productImportPriceSchema,
  is_default: z.boolean(),
})

export const productImportProductSchema = z.object({
  name: z.string(),
  category_name: z.string().nullable(),
  image_url: z.string().nullable(),
  collection_source: z.string(),
  is_designers_choice: z.boolean(),
  price_source: z.string().nullable(),
  shown_at_price: productImportPriceSchema.nullable(),
  collections: z.array(z.string()),
  variants: z.array(productImportVariantSchema),
})

export const productImportRowSchema = z.object({
  row_number: z.number().int().positive(),
  status: z.enum(['ready', 'imported', 'updated', 'skipped', 'invalid']),
  reason: z.string().nullable(),
  action: z.enum(['create', 'update']).nullable(),
  product_id: z.number().int().positive().nullable(),
  source_system: productImportSourceSystemSchema,
  source_key: z.string(),
  product: productImportProductSchema,
})

export const productImportSummarySchema = z.object({
  ready: z.number().int().nonnegative(),
  imported: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  planned_created: z.number().int().nonnegative().default(0),
  planned_updated: z.number().int().nonnegative().default(0),
  image_failures: z.number().int().nonnegative(),
  collection_banner_warnings: z.number().int().nonnegative(),
})

export const productImportImageFailureSchema = z.object({
  line_number: z.number().int().nonnegative(),
  source_key: z.string(),
  name: z.string(),
  image_url: z.string(),
  reason: z.string(),
})

export const productImportCollectionBannerWarningSchema = z.object({
  line_number: z.number().int().nonnegative(),
  source_key: z.string(),
  name: z.string(),
  image_url: z.string(),
  reason: z.string(),
})

export const productImportWebsiteNavigationItemSchema = z.object({
  collection_id: z.number().int().positive(),
  label: z.string(),
  sort_order: z.number().int().nonnegative(),
})

export const productImportWebsiteNavigationSchema = z.object({
  mode: productImportWebsiteNavigationModeSchema,
  status: z.enum(['pending', 'updated', 'unchanged', 'skipped']),
  message: z.string(),
  reason: z.string().nullable(),
  nav_items_count: z.number().int().nonnegative(),
  added: z.number().int().nonnegative(),
  reordered: z.number().int().nonnegative(),
  items: z.array(productImportWebsiteNavigationItemSchema),
})

export const productImportResponseSchema = z.object({
  source_system: productImportSourceSystemSchema,
  source_label: z.string(),
  import_scope: productImportScopeSchema,
  import_scope_label: z.string(),
  expected_headers: z.array(z.string()),
  summary: productImportSummarySchema,
  created_product_ids: z.array(z.number().int().positive()),
  updated_product_ids: z.array(z.number().int().positive()),
  image_failures: z.array(productImportImageFailureSchema),
  collection_banner_warnings: z.array(productImportCollectionBannerWarningSchema),
  website_navigation: productImportWebsiteNavigationSchema,
  rows: z.array(productImportRowSchema),
  rows_returned: z.number().int().nonnegative().optional(),
  rows_truncated: z.boolean().default(false),
})

export const productImportAttemptProgressSchema = z.object({
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().int().min(0).max(100),
})

export const productImportAttemptSchema = z.object({
  id: z.uuid(),
  status: z.enum(['queued', 'running', 'completed', 'failed']),
  stage: z.enum([
    'queued',
    'reading_file',
    'preparing_images',
    'importing_products',
    'finalizing',
    'completed',
    'failed',
  ]),
  stage_label: z.string(),
  message: z.string().nullable(),
  source_system: productImportSourceSystemSchema,
  source_label: z.string(),
  import_scope: productImportScopeSchema,
  import_scope_label: z.string(),
  website_navigation_mode: productImportWebsiteNavigationModeSchema,
  progress: productImportAttemptProgressSchema,
  result: productImportResponseSchema.nullable(),
  error: z
    .object({
      message: z.string(),
    })
    .nullable(),
  queued_at: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
})

export const productImportActiveAttemptResponseSchema = z.object({
  attempt: productImportAttemptSchema.nullable(),
})
